import './App.css';
import '@ant-design/v5-patch-for-react-19';
import {Auth} from './auth/Auth';
import {Logout} from'./auth/Logout';
import {TabController} from './ui/TabController';
import {useState, useCallback, useEffect} from 'react'
import { BrowserRouter, Routes, Route} from "react-router";
import {InventoryTab} from "./ui/inventory/InventoryTab";
import {
    setPermissions,
    checkPerm
} from "./auth/sessionTools";
import {SalesTab} from "./ui/sales/SalesTab";
import {BuyerTab} from "./ui/buyer/BuyerTab";
import {ReturnTab} from "./ui/returns/ReturnTab";
import {AccountingTab} from "./ui/accounting/AccountingTab";
import {ProductView} from "./ui/inventory/ProductView";
import {GeneralTab} from "./ui/general/GeneralTab";
import {InvoiceView} from "./ui/sales/InvoiceView";
import {FulfillmentTab} from "./ui/fulfillment/FulfillmentTab";
import {BuyerView} from "./ui/buyer/BuyerView";
import {VendorView} from "./ui/Contacts/VendorView";
import {PurchaseOrderView} from "./ui/fulfillment/PurchaseOrderView";
import {POLineItemView} from "./ui/fulfillment/POLineItemView";
import {AdminTab} from "./ui/Admin/AdminTab";
import {ContactTab} from "./ui/Contacts/ContactTab";
import {ProfitReport} from "./ui/accounting/ProfitReport";
import {CogsReport} from "./ui/accounting/CogsReport";
import {InventoryView} from "./ui/inventory/InventoryView";
import {TaskTab} from "./ui/tasks/TaskTab";
import {TaskView} from "./ui/tasks/TaskView";
import {REFRESH_PERIOD, PlantStageType} from "./api/Constants";
import {toMs} from "./util/timeutils";
import {CultivationTab} from "./ui/cultivation/CultivationTab";
import {ManufacturingTab} from "./ui/manufacturing/ManufacturingTab";
import {OperationsTab} from "./ui/operations/OperationsTab";
import {GraphicsTab} from "./ui/graphics/GraphicsTab";
import {RetailTab} from "./ui/retail/RetailTab";
import {OutsideSalesTab} from "./ui/outsideSales/OutsideSalesTab";
import {CultivationApp} from "./ui/cultivation/CultivationApp";
import {getCultivationRooms, getTaskCount} from "./api/Api";
import {serverType} from "./api/server";
import {BuyerBalanceView} from "./ui/buyer/BuyerBalanceView";
import {BuyerPaymentView} from "./ui/buyer/BuyerPaymentView";
import {BankAccountView} from "./ui/accounting/BankAccountView";
import {TransactionView} from "./ui/accounting/TransactionView";
import {CloneTab} from "./ui/cultivation/CloneTab";
import {CloneView} from "./ui/cultivation/CloneView";
import {GeneticView} from "./ui/cultivation/GeneticView";
import {Flower1RoomId, Flower2RoomId, Flower3RoomId, Flower4RoomId,
    Harvest1RoomId, Harvest2RoomId} from "./api/RoomConstants"
import {HarvestTab} from "./ui/cultivation/HarvestTab";
import {AllBatchView} from "./ui/cultivation/AllBatchView";
import {BatchView} from "./ui/cultivation/BatchView";
import {BatchReport} from "./ui/accounting/BatchReport";
import {PlantSearch} from "./ui/cultivation/PlantSearch";
import {UserView} from "./ui/user/UserView";
import {PurchaseOrderReport} from "./ui/accounting/PurchaseOrderReport";
import {CashFlowReport} from "./ui/accounting/CashFlowReport";
import {PlantStrains} from "./ui/cultivation/PlantStrains";
import {PlantStrainView} from "./ui/cultivation/PlantStrainView";
import {HarvestReport} from "./ui/operations/HarvestReport";

function App() {
    const [session, setSession] = useState({});
    const [authorized, setAuthorized] = useState(false);
    const [numTasks, setNumTasks] = useState(0);
    const [roomMap, setRoomMap] = useState({});

    const authComplete = useCallback((access) => {
        console.log(`authComplete ${JSON.stringify(access)}`);
        setAuthorized(true);
        setPermissions(access);
        setSession(access);
    }, []);

    const onFail = useCallback(() => {
        setAuthorized(false);
        console.log(`login failed`);
    }, []);

    const [refresh, setRefresh] = useState(0);

    const onRefresh = useCallback(() => {
        setRefresh(refresh+1)
    }, [refresh]);

    const setSessionEmail = (email) => {
        const nextSession = {
            ...session,
            email: email
        }
        console.log(`setting email to ${email}`)
        setSession(nextSession);
    }

    useEffect(() => {
        const refreshInterval = setInterval(() => {
            onRefresh();
        }, toMs(REFRESH_PERIOD));
        return () => {
            clearInterval(refreshInterval);
        }
    }, [onRefresh]);

    useEffect(() => {
        if (authorized === false) {
            return;
        }
        getTaskCount().then(r => {
            const {numTasks} = r;
            setNumTasks(numTasks);
        }).catch(e => {
            if (e.status === 401) {
                onFail();
            }
        });
    }, [refresh, authorized, onFail]);

    useEffect(() => {
        if (authorized === false) {
            return;
        }
        // if (serverType === "prod") {
        //     // disable this for prod for now
        //     return;
        // }

        getCultivationRooms().then(res => {
            // make this into an object
            const roomMap = {}
            res.forEach(r => {
                roomMap[r.id] = {...r};
            });
            setRoomMap(roomMap);
        }).catch(e => {
            if (e.status === 401) {
                onFail();
            }
        });
    }, [refresh, authorized, onFail])

    const LogoutButton = <div style={{display: 'grid', gridTemplateColumns: '5fr 1fr'}}>
        <div />
        <div style={{display: 'grid', gridTemplateColumns: '1fr auto'}}>
            <div />
            <div><Logout onLogout={onFail}/></div>
        </div>
    </div>

    const isSales = checkPerm(session, "sales");
    const isAccounting = checkPerm(session, "accounting");
    const hasExecutivePerm = checkPerm(session, "executive");
    const hasFulfillmentPerm = checkPerm(session, "fulfillment");
    const hasInventory = checkPerm(session, "inventory");
    const isCultivation = checkPerm(session, "cultivation");
    const isManufacturing = checkPerm(session, "manufacturing");
    const isOps = checkPerm(session, "operations");
    const isGraphics = checkPerm(session, "graphics");
    const isTrapcon = checkPerm(session, "trapconretail");
    const isOutsideSales = checkPerm(session, "outsidesales");

    let titleContent;
    let appStyle
    switch (serverType) {
        case 'dev':
            titleContent = <div style={{color: 'red'}}>Developmental Full Spectrum</div>
            appStyle = {backgroundColor: '#afafaf'}
            break;
        case 'prod':
            titleContent = `Full Spectrum`;
            appStyle = {}
            break;
        case 'staging':
        default:
            titleContent = <div style={{color: 'red'}}>STAGING Full Spectrum</div>
            appStyle = {backgroundColor: '#afafaf'}
            break;
    }

    const io = {
        refresh,
        onRefresh,
        onApiFail: onFail
    }

    return (
        <div style={appStyle} className="App">
            <b>{titleContent}</b>
            <p />
            {authorized ? LogoutButton : <div />}
            <Auth authorized={authorized} onComplete={authComplete} onFail={onFail}>
                <BrowserRouter>
                    <Routes>
                        <Route path={'/cultivationapp/'} element={<CultivationApp session={session} roomMap={roomMap} numTasks={numTasks} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} >
                            <Route index element={<CloneTab room={roomMap[Flower1RoomId]} stage={PlantStageType.Flower} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'strains'} element={<PlantStrains io={io} />} />
                            <Route path={'flower1'} element={<CloneTab room={roomMap[Flower1RoomId]} stage={PlantStageType.Flower} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'flower2'} element={<CloneTab room={roomMap[Flower2RoomId]} stage={PlantStageType.Flower} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'flower3'} element={<CloneTab room={roomMap[Flower3RoomId]} stage={PlantStageType.Flower} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'flower4'} element={<CloneTab room={roomMap[Flower4RoomId]} stage={PlantStageType.Flower} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'harvest1'} element={<HarvestTab session={session} room={roomMap[Harvest1RoomId]} stage={PlantStageType.Harvest} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'harvest2'} element={<HarvestTab session={session} room={roomMap[Harvest2RoomId]} stage={PlantStageType.Harvest} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'batch/:batchId'} element={<BatchView session={session} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>}/>
                            <Route path={'batch'} element={<AllBatchView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'search'} element={<PlantSearch roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'plant/:plantId'} element={<CloneView session={session} roomMap={roomMap} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'genetic/:geneticId'} element={<GeneticView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'strain/:strainId'} element={<PlantStrainView io={io}/>} />
                        </Route>
                        <Route path={'/'} element={<TabController onApiFail={onFail} session={session} numTasks={numTasks}/>}>
                            <Route index element={<GeneralTab session={session} setSessionEmail={setSessionEmail} onApiFail={onFail}/>} />
                            <Route path={'general'} element={<GeneralTab session={session} setSessionEmail={setSessionEmail} onApiFail={onFail}/>} />
                            <Route path={'tasks'} element={<TaskTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'contacts'} element={<ContactTab refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            {hasInventory && <Route path={'inventory'} element={<InventoryTab refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />}
                            {isSales && <Route path={'sales'} element={<SalesTab session={session} onApiFail={onFail} refresh={refresh} onRefresh={onRefresh} />} />}
                            {isOutsideSales && <Route path={'outsidesales'} element={<OutsideSalesTab session={session} onApiFail={onFail} refresh={refresh} onRefresh={onRefresh} />} />}
                            {isSales && <Route path={'buyers'} element={<BuyerTab refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {isSales && <Route path={'returns'} element={<ReturnTab refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {hasFulfillmentPerm && <Route path={'fulfillment'} element={<FulfillmentTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />}
                            {isCultivation && <Route path={'cultivation'} element={<CultivationTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />}
                            {isManufacturing && <Route path={'manufacturing'} element={<ManufacturingTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {isOps && <Route path={'operations'} element={<OperationsTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {isGraphics && <Route path={'graphics'} element={<GraphicsTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {isTrapcon  && <Route path={'trapconretail'} element={<RetailTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {isAccounting && <Route path={'accounting'} element={<AccountingTab session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />}
                            {hasExecutivePerm && <Route path={'admin'} element={<AdminTab refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} /> } />}
                            <Route path={'product/:productId'} element={<ProductView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'invoice/:invoiceId'} element={<InvoiceView session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={`purchaseorder/:purchaseId/lineItem/:lineId`} element={<POLineItemView onApiFail={onFail} refresh={refresh} onRefresh={onRefresh} />} />
                            <Route path={`purchaseorder/:purchaseId`} element={<PurchaseOrderView session={session} onApiFail={onFail} refresh={refresh} onRefresh={onRefresh}/>} />
                            <Route path={'buyer/:buyerId'} element={<BuyerView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'buyer/:buyerId/balance'} element={<BuyerBalanceView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'buyer/:buyerId/payment'} element={<BuyerPaymentView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'vendor/:vendorId'} element={<VendorView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'report/profit'} element={<ProfitReport refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'report/cogs'} element={<CogsReport refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'report/purchaseorder'} element={<PurchaseOrderReport refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'report/cashflow'} element={<CashFlowReport refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'report/batch'} element={<BatchReport refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} roomMap={roomMap} />} />
                            <Route path={'report/harvest'} element={<HarvestReport roomMap={roomMap} io={io} />} />
                            <Route path={'inventory/:inventoryId'} element={<InventoryView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'task/:taskId'} element={<TaskView session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} />} />
                            <Route path={'bank/:bankId'} element={<BankAccountView session={session} refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            <Route path={'transaction/:transactionId'} element={<TransactionView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail}/>} />
                            {hasExecutivePerm && <Route path={'user/:userId'} element={<UserView refresh={refresh} onRefresh={onRefresh} onApiFail={onFail} /> } />}
                            <Route path={'*'} element={<div>Not Found</div>} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </Auth>
        </div>
    );
}

export default App;
